import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Dropzone from 'react-dropzone';

import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';
import IconButton from 'components/common/IconButton';
import IconCamera from 'components/icons/IconCamera';
import IconUpload from 'components/icons/IconUpload';
import IconUploadBold from 'components/icons/IconUploadBold';
import IconCross from 'components/icons/IconCross';
import IconClip from 'components/icons/IconClip';
import IconPlus from 'components/icons/IconPlus';
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from 'components/common/Dialog';
import Cropper from 'react-easy-crop';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

import getCroppedImage from 'utils/getCroppedImage';

import { addNotification } from 'redux/modules/notifications';

const styles = theme => ({
  root: {
    display: 'flex',
    width: '100%',
  },
  input: {
    position: 'relative',
    border: `1px solid ${theme.palette.grey[600]}`,
    borderRight: 'none',
    borderRadius: '4px 0 0 4px',
    backgroundColor: theme.palette.common.white,
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
    lineHeight: pxToEm(16, 14),
    height: pxToEm(16, 14),
    padding: '9px 15px 10px 15px',
    color: theme.palette.secondary[800],
    fontFamily: theme.typography.fontFamily,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  inputUploaded: {
    borderRight: `1px solid ${theme.palette.grey[600]}`,
    borderRadius: 4,
  },
  inputDisabled: {
    borderColor: `rgba(${hexToRgb(theme.palette.primary.A100)}, 0.5)`,
  },
  button: {
    width: '100%',
    borderRadius: '0 4px 4px 0',
    minHeight: 37,
    fontWeight: 700,
    fontSize: pxToRem(12),
    background: theme.palette.grey[600],
    color: theme.palette.common.white,
    '&:hover': {
      background: `rgba(${hexToRgb(theme.palette.grey[600])}, 0.5)`,
    },
  },
  dropzone: {

  },
  dropzoneAccept: {
    '& $gridImage': {
      borderColor: theme.palette.primary[500],
    },
    '& $import': {
      borderColor: theme.palette.primary[500],
    },
    '& $document': {
      borderColor: theme.palette.primary[500],
    },
    '& $contentAcceptClassName': {
      borderColor: theme.palette.primary[500],
      backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
    },
  },
  contentAcceptClassName: {
    transition: theme.transitions.create(['border-color', 'background-color'], {
      duration: theme.transitions.duration.short,
    }),
  },
  progress: {
    width: 30,
    height: 30,
  },
  inputGrid: {
    width: '70%',
    flexShrink: 1,
  },
  buttonGrid: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '22.2%',
    flexBasis: '22.2%',
    [theme.breakpoints.down('xs')]: { 
      maxWidth: '35%',
      flexBasis: '35%',
    },
  },
  gridImage: {
    padding: 24,
    borderRadius: 4,
    border: `1px dashed ${theme.palette.primary.A400}`,
    transition: theme.transitions.create(['border-color'], {
      duration: theme.transitions.duration.short,
    }),
  },
  gridImageNoControls: {
    padding: 16,
  },
  gridControls: {
    height: '100%',
  },
  image: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 112,
    height: 112,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.active[50]}`,
  },
  imageNoControls: {
    margin: '0 auto',
  },
  imagePreview: {
    maxWidth: '100%',
    width: '100%',
  },
  secondaryImages: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  secondaryImage: {
    width: 48,
    height: 48,
    margin: '8px 4px 0 4px',
    borderRadius: 10,
    overflow: 'hidden',
  },
  iconCamera: {
    width: 49,
    height: 44,
    color: theme.palette.primary.A400,
  },
  typographyLabel: {
    lineHeight: pxToEm(18, 12),
  },
  import: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    width: 140,
    height: 140,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
    border: `1px dashed ${theme.palette.primary.A400}`,
    transition: theme.transitions.create(['border-color'], {
      duration: theme.transitions.duration.short,
    }),
    '& $typographyLabel': {
      marginTop: 10,
      opacity: 0.5,
    },
  },
  document: {
    padding: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: theme.palette.grey[100],
    border: `1px dashed ${theme.palette.primary.A400}`,
    transition: theme.transitions.create(['border-color'], {
      duration: theme.transitions.duration.short,
    }),
    '& $typographyLabel': {
      opacity: 0.5,
    },
    '& $typographyValue': {
      width: 'auto',
      marginTop: 0,
    },
  },
  iconUpload: {
    width: 36,
    height: 40,
    marginTop: 10,
    color: theme.palette.primary.A400,
  },
  iconUploadBold: {
    width: 14,
    height: 17,
    marginRight: 10,
    color: theme.palette.primary.A400,
  },
  typographyValue: {
    lineHeight: pxToEm(22.1, 12),
    marginTop: 10,
    opacity: 0.5,
    width: 'calc(100% - 20px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  buttonClear: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  iconCross: {
    width: 12,
    height: 12,
  },
  buttonUploadImageColumn: {
    margin: '8px auto 0',
    display: 'flex',
  },
  iconClip: {
    width: 16,
    height: 20,
  },
  dialogContent: {
    position: 'relative',
    height: 600,
  },
  cropperContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    userSelect: 'none',
    overflow: 'hidden',
    touchAction: 'none',
    cursor: 'move',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cropArea: {
    border:  `1px solid ${theme.palette.primary[500]}!important`,
  },
  iconButton: {
    verticalAlign: 'middle',
  },
  iconPlus: {
    width: 16,
    height: 16,
  },
  iconMinus: {
    width: 16,
    height: 3,
    backgroundColor: theme.palette.grey[600],
  },
  action: {
    '&:first-child': {
      position: 'absolute',
      left: 4,
      top: 2,
    },
  },
});

class UploadField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dialogOpen: false,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
      initialValue: null,
    };

    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogCancel = this.onDialogCancel.bind(this);
    this.onCropChange = this.onCropChange.bind(this);
    this.onZoomOut = this.onZoomOut.bind(this);
    this.onZoomIn = this.onZoomIn.bind(this);
    this.onZoomChange = this.onZoomChange.bind(this);
    this.onCropComplete = this.onCropComplete.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.multiple && this.props.input.value && nextProps.input.value
      && this.props.input.value.preview !== nextProps.input.value.preview
      && nextProps.input.value.type) {
      this.setState({
        dialogOpen: true,
        initialValue: this.props.input.value,
      });
    }
  }

  onCropChange(crop) {
    this.setState({ crop });
  }

  onZoomOut() {
    this.setState({ zoom: this.state.zoom - 0.1 });
  }

  onZoomIn() {
    this.setState({ zoom: this.state.zoom + 0.1 });
  }

  onZoomChange(zoom) {
    this.setState({ zoom });
  }

  onCropComplete(croppedArea, croppedAreaPixels) {
    const { zoom } = this.state;
    this.setState({ croppedAreaPixels: zoom < 1 ? {
      ...croppedAreaPixels,
      width: croppedAreaPixels.width / zoom,
      height: croppedAreaPixels.height / zoom,
    } : croppedAreaPixels });
  }

  onDialogClose() {
    this.setState({
      initialValue: null,
      dialogOpen: false,
    });
  }

  onDialogCancel() {
    const { initialValue } = this.state;
    this.props.input.onChange(initialValue);
    this.onDialogClose();
  }

  render() {
    const {
      input,
      disabled,
      loading,
      uploaded,
      variant,
      maxSize,
      minWidth,
      minHeight,
      cropWidth,
      cropHeight,
      label,
      accept,
      actions,
      showControls,
      showClearButton,
      multiple,
      maxLength,
      controlsDirection,
      renderContent,
      classes,
    } = this.props;
    const { dialogOpen, crop, zoom, croppedAreaPixels } = this.state;
    let dropzoneRef;
    let previewSrc;
    let uploadImageText = 'Upload Image';
    if (multiple) {
      uploadImageText = 'Upload Images';
      if (input.value[0] && input.value[0].preview) {
        if (input.value[0].type) {
          previewSrc = input.value[0].preview;
        } else {
          previewSrc = process.env.REACT_APP_API_ROOT
            ? `${process.env.REACT_APP_API_ROOT}${input.value[0].preview}`
            : input.value[0].preview;
        }
      }
    } else if (input && input.value && input.value.preview) {
      previewSrc = input.value.preview;
    }
    return (
      <FormControl className={classes.root}>
        <Dropzone
          className={classes.dropzone}
          multiple={multiple}
          acceptClassName={classes.dropzoneAccept}
          onDrop={(acceptedFiles, rejectedFiles) => {
            const rejectedFile = rejectedFiles[0];
            if (acceptedFiles.length > 0) {
              if (multiple && maxLength
                && maxLength < (input.value.length + acceptedFiles.length)) {
                actions.addNotification({
                  message: `Maximum files number is ${maxLength}`,
                  variant: 'warning',
                });
                return;
              }
              if (minWidth || minHeight) {
                let processedFiles = [];
                let loadedImages = 0;
                acceptedFiles.forEach((acceptedFile) => {
                  const image = new Image();
                  image.addEventListener('load', () => {
                    loadedImages += 1;
                    if ((!minWidth || image.width >= minWidth)
                      && (!minHeight || image.height >= minHeight)) {
                      if (cropWidth && cropHeight) {
                        const canvas = document.createElement('canvas');
                        const context = canvas.getContext('2d');
                        canvas.width = cropWidth;
                        canvas.height = cropHeight;
                        const cropWidthHeight = Math.min(image.width, image.height);
                        const sourceX = (image.width / 2) - (cropWidthHeight / 2);
                        const sourceY = (image.height / 2) - (cropWidthHeight / 2);
                        const sourceWidth = cropWidthHeight;
                        const sourceHeight = cropWidthHeight;
                        const destWidth = cropWidth;
                        const destHeight = cropHeight;
                        const destX = (canvas.width / 2) - (destWidth / 2);
                        const destY = (canvas.height / 2) - (destHeight / 2);
                        context.drawImage(image, sourceX, sourceY, sourceWidth,
                          sourceHeight, destX, destY, destWidth, destHeight);
                        canvas.toBlob((blob) => {
                          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                            const croppedAcceptedFile = blob;
                            croppedAcceptedFile.lastModifiedDate = new Date();
                            croppedAcceptedFile.preview = URL.createObjectURL(blob);
                            croppedAcceptedFile.name = acceptedFile.name;
                            croppedAcceptedFile.noFileConstructor = true;
                            processedFiles = [
                              ...processedFiles,
                              croppedAcceptedFile,
                            ];
                            if (loadedImages === acceptedFiles.length) {
                              if (multiple) {
                                input.onChange([
                                  ...input.value,
                                  ...processedFiles,
                                ]);
                              } else {
                                input.onChange(processedFiles[0]);
                              }
                            }
                          } else {
                            const croppedAcceptedFile = new File(
                              [blob],
                              acceptedFile.name,
                              { type: acceptedFile.type, lastModified: Date.now() },
                            );
                            croppedAcceptedFile.preview = URL.createObjectURL(blob);
                            processedFiles = [
                              ...processedFiles,
                              croppedAcceptedFile,
                            ];
                            if (loadedImages === acceptedFiles.length) {
                              if (multiple) {
                                input.onChange([
                                  ...input.value,
                                  ...processedFiles,
                                ]);
                              } else {
                                input.onChange(processedFiles[0]);
                              }
                            }
                          }
                        });
                      } else if (loadedImages === acceptedFiles.length) {
                        if (multiple) {
                          input.onChange([
                            ...input.value,
                            ...acceptedFiles,
                          ]);
                        } else {
                          input.onChange(acceptedFiles[0]);
                        }
                      }
                    } else {
                      actions.addNotification({
                        message: 'Invalid image size',
                        variant: 'warning',
                      });
                    }
                  });
                  image.src = acceptedFile.preview;
                });
              } else if (multiple) {
                input.onChange([
                  ...input.value,
                  ...acceptedFiles,
                ]);
              } else {
                input.onChange(acceptedFiles[0]);
              }
            }
            if (rejectedFile) {
              if (rejectedFile.type
                && rejectedFile.type.length > 0
                && (accept && accept.indexOf(rejectedFile.type) === -1)) {
                actions.addNotification({
                  message: 'Invalid file type',
                  variant: 'warning',
                });
              } else if (rejectedFile.size
                && typeof rejectedFile.size === 'number'
                && maxSize
                && rejectedFile.size > maxSize) {
                actions.addNotification({
                  message: 'Invalid file size',
                  variant: 'warning',
                });
              }
            }
          }}
          ref={(node) => { dropzoneRef = node; }}
          disabled={disabled || loading || uploaded}
          {...accept ? { accept } : {}}
          {...maxSize ? { maxSize } : {}}
          disableClick
        >
          {variant === 'default' &&
            <Grid
              container
              justify="center"
              wrap="nowrap"
              spacing={0}
            >
              <Grid
                item
                xs={12}
                className={classNames({
                  [classes.inputGrid]: !uploaded,
                })}
              >
                <div
                  className={classNames(classes.input, {
                    [classes.inputDisabled]: loading,
                    [classes.inputUploaded]: uploaded,
                  })}
                >
                  {input.value &&
                    input.value.name
                  }
                </div>
              </Grid>
              {!uploaded && (
                <Grid
                  item
                  xs={3}
                  className={classes.buttonGrid}
                >
                  <Button
                    className={classes.button}
                    onClick={(event) => {
                      event.stopPropagation();
                      dropzoneRef.open();
                    }}
                    disabled={disabled || loading}
                  >
                    Choose File
                  </Button>
                  {loading && !uploaded &&
                    <Progress
                      size={30}
                      className={classes.progress}
                    />
                  }
                </Grid>
              )}
            </Grid>
          }
          {variant === 'image' &&
            <Fragment>
              <Grid
                className={classNames(classes.gridImage, {
                  [classes.gridImageNoControls]: !showControls || (showControls && controlsDirection === 'column'),
                })}
                container
                justify="center"
                wrap="nowrap"
                spacing={0}
                {...((!showControls && label) || (showControls && controlsDirection === 'column')) ? {
                  direction: 'column',
                  alignItems: 'center',
                  spacing: 8,
                } : {}}
              >
                <Grid
                  item
                  xs={showControls && controlsDirection === 'row' ? 6 : 12}
                >
                  <div
                    className={classNames(classes.image, {
                      [classes.imageNoControls]: !showControls || (showControls && controlsDirection === 'column'),
                    })}
                    role="presentation"
                    onClick={(event) => {
                      event.stopPropagation();
                      dropzoneRef.open();
                    }}
                  >
                    {previewSrc ? (
                      <Fragment>
                        <img
                          className={classes.imagePreview}
                          src={previewSrc}
                          alt={input.name}
                        />
                        {showClearButton &&
                          <IconButton
                            className={classes.buttonClear}
                            onClick={(event) => {
                              event.stopPropagation();
                              input.onChange(null);
                            }}
                            tooltipContent="Clear Image"
                            tooltipPlacement="left"
                            disabled={disabled}
                          >
                            <IconCross className={classes.iconCross} />
                          </IconButton>
                        }

                      </Fragment>
                    ) : (
                      <IconCamera className={classes.iconCamera} />
                    )}
                  </div>
                  {(multiple && input.value.length > 1) &&
                    <div className={classes.secondaryImages}>
                      {input.value.slice(1).map((item) => {
                        let itemPreviewSrc;
                        if (item.type) {
                          itemPreviewSrc = item.preview;
                        } else {
                          itemPreviewSrc = process.env.REACT_APP_API_ROOT
                            ? `${process.env.REACT_APP_API_ROOT}${item.preview}`
                            : item.preview;
                        }
                        return (
                          <div className={classes.secondaryImage} key={item.preview}>
                            <img
                              className={classes.imagePreview}
                              src={itemPreviewSrc}
                              alt={input.name}
                            />
                      buttonUploadImage    </div>
                        );
                      })}
                    </div>
                  }
                </Grid>
                {((showControls && !multiple)
                  || (showControls && multiple && input.value.length < maxLength)) &&
                  <Grid
                    item
                    xs={controlsDirection === 'row' ? 6 : 12}
                  >
                    <Grid
                      className={classes.gridControls}
                      container
                      justify="space-between"
                      direction="column"
                      spacing={0}
                    >
                      {((label && multiple && !input.value) || (label && !multiple)) &&
                        <Grid item>
                          <Typography className={classes.typographyLabel} variant="caption">
                            {label}
                          </Typography>
                        </Grid>
                      }
                      <Grid item>
                        <Button
                          className={classNames({
                            [classes.buttonUploadImageColumn]: controlsDirection === 'column',
                          })}
                          variant="raised"
                          onClick={(event) => {
                            event.stopPropagation();
                            dropzoneRef.open();
                          }}
                          disabled={disabled || loading}
                        >
                          {(input.value && input.value.name) ? 'Change Image' : uploadImageText}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                }
                {(!showControls && label && !input.value) &&
                  <Grid item>
                    <Typography className={classes.typographyLabel} variant="caption" align="center">
                      {label}
                    </Typography>
                  </Grid>
                }
              </Grid>
              <Dialog
                open={dialogOpen}
                onClose={this.onDialogClose}
                maxWidth="md"
              >
                <DialogTitle>
                  <Typography
                    variant="title"
                    color="secondary"
                    align="center"
                  >
                    Edit Image
                  </Typography>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                  <Cropper
                    classes={{
                      containerClassName: classes.cropperContainer,
                      cropAreaClassName: classes.cropArea,
                    }}
                    image={input.value.preview}
                    crop={crop}
                    zoom={zoom}
                    minZoom={0.7}
                    aspect={5 / 2}
                    onCropChange={this.onCropChange}
                    onZoomChange={this.onZoomChange}
                    onCropComplete={this.onCropComplete}
                  />
                </DialogContent>
                <DialogActions
                  classes={{
                    action: classes.action,
                  }}
                >
                  <div>
                    <IconButton
                      className={classes.iconButton}
                      onClick={this.onZoomOut}
                      tooltipContent="Zoom Out"
                      disabled={zoom <= 0.7}
                    >
                      <div className={classes.iconMinus} />
                    </IconButton>
                    <IconButton
                      className={classes.iconButton}
                      onClick={this.onZoomIn}
                      tooltipContent="Zoom In"
                    >
                      <IconPlus className={classes.iconPlus} />
                    </IconButton>
                  </div>
                  <Button
                    onClick={this.onDialogCancel}
                    variant="raised"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      getCroppedImage(
                        input.value.preview,
                        croppedAreaPixels,
                        input.value.type,
                      ).then(res => {
                        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                          const croppedAcceptedFile = res;
                          croppedAcceptedFile.lastModifiedDate = new Date();
                          croppedAcceptedFile.preview = URL.createObjectURL(res);
                          croppedAcceptedFile.name = input.value.name;
                          croppedAcceptedFile.noFileConstructor = true;
                          input.onChange(croppedAcceptedFile);
                        } else {
                          const croppedAcceptedFile = new File(
                            [res],
                            input.value.name,
                            { type: input.value.type, lastModified: Date.now() },
                          );
                          croppedAcceptedFile.preview = URL.createObjectURL(res);
                          input.onChange(croppedAcceptedFile);
                        }
                        this.onDialogClose();
                      });                  
  
                    }}
                    variant="raised"
                    color="primary"
                    disabled={!croppedAreaPixels}
                  >
                    Done
                  </Button>
                </DialogActions>
              </Dialog>
            </Fragment>
          }
          {variant === 'import' &&
            <div
              className={classes.import}
              role="presentation"
              onClick={(event) => {
                event.stopPropagation();
                dropzoneRef.open();
              }}
            >
              <IconUpload className={classes.iconUpload} />
              {label && !input.value.name &&
                <Typography className={classes.typographyLabel} variant="caption">
                  {label}
                </Typography>
              }
              {input.value.name &&
                <Typography className={classes.typographyValue} variant="caption">
                  {input.value.name}
                </Typography>
              }
              {loading && !uploaded &&
                <Progress
                  size={30}
                  className={classes.progress}
                />
              }
            </div>
          }
          {variant === 'document' &&
            <div
              className={classes.document}
              role="presentation"
              onClick={(event) => {
                event.stopPropagation();
                dropzoneRef.open();
              }}
            >
              <IconUploadBold className={classes.iconUploadBold} />
              {label && !input.value.name &&
                <Typography className={classes.typographyLabel} variant="caption">
                  {label}
                </Typography>
              }
              {input.value.name &&
                <Typography className={classes.typographyValue} variant="caption">
                  {input.value.name}
                </Typography>
              }
              {loading && !uploaded &&
                <Progress
                  size={30}
                  className={classes.progress}
                />
              }
            </div>
          }
          {variant === 'icon' &&
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                dropzoneRef.open();
              }}
              tooltipContent={label}
            >
              <IconClip className={classes.iconClip} />
            </IconButton>
          }
          {(variant === 'container' && renderContent) && (
            renderContent(input, classes.contentAcceptClassName)
          )}
        </Dropzone>
      </FormControl>
    );
  }
}

UploadField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.shape({
        name: PropTypes.string,
      }),
      PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
      })),
      PropTypes.string,
    ]),
    onChange: PropTypes.func,
  }).isRequired,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  uploaded: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'image', 'import', 'document', 'icon', 'container']),
  renderContent: PropTypes.func,
  maxSize: PropTypes.number,
  minWidth: PropTypes.number,
  minHeight: PropTypes.number,
  cropWidth: PropTypes.number,
  cropHeight: PropTypes.number,
  accept: PropTypes.string,
  actions: PropTypes.shape({
    addNotification: PropTypes.func.isRequired,
  }).isRequired,
  showControls: PropTypes.bool,
  controlsDirection: PropTypes.oneOf(['row', 'column']),
  showClearButton: PropTypes.bool,
  multiple: PropTypes.bool,
  maxLength: PropTypes.number,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    input: PropTypes.string.isRequired,
    inputDisabled: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    dropzone: PropTypes.string.isRequired,
    dropzoneAccept: PropTypes.string.isRequired,
    progress: PropTypes.string.isRequired,
    inputGrid: PropTypes.string.isRequired,
    buttonGrid: PropTypes.string.isRequired,
    gridImage: PropTypes.string.isRequired,
    gridImageNoControls: PropTypes.string.isRequired,
    gridControls: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    imageNoControls: PropTypes.string.isRequired,
    iconCamera: PropTypes.string.isRequired,
    typographyLabel: PropTypes.string.isRequired,
    imagePreview: PropTypes.string.isRequired,
    secondaryImage: PropTypes.string.isRequired,
    import: PropTypes.string.isRequired,
    document: PropTypes.string.isRequired,
    iconUpload: PropTypes.string.isRequired,
    iconUploadBold: PropTypes.string.isRequired,
    typographyValue: PropTypes.string.isRequired,
  }).isRequired,
};

UploadField.defaultProps = {
  disabled: false,
  loading: false,
  uploaded: false,
  variant: 'default',
  minWidth: null,
  minHeight: null,
  cropWidth: null,
  cropHeight: null,
  maxSize: null,
  label: null,
  accept: null,
  showControls: true,
  showClearButton: false,
  multiple: false,
  maxLength: null,
  controlsDirection: 'row',
  renderContent: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    addNotification,
  }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(UploadField),
);



// WEBPACK FOOTER //
// ./src/components/fields/UploadField.js